import { MembershipPaymentWidget } from '@marpple/mco-membership-ui';
import { $, html, type Html, Page, View } from 'rune-ts';
import { ConfigSentryF } from '../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { createOnClickInicisTerms } from '../../../../../modules/MShop/App/Mypage/MembershipPayMethod/component/terms/InicisTerms';
import { createOnClickPersonalInfoOutsourcing } from '../../../../../modules/MShop/App/Mypage/MembershipPayMethod/component/terms/PersonalInfoOutsourcingTerms';
import { createOnClickThirdPartyConsent } from '../../../../../modules/MShop/App/Mypage/MembershipPayMethod/component/terms/ThirdPartyConsentTerms';
import { MShopUtilF } from '../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { PrivacyPopUpTmplS } from '../../../../../modules/Privacy/PopUp/S/Tmpl/module/PrivacyPopUpTmplS';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { MembershipErrorEnable } from '../../../features/Membership/error/error_enable';
import { MembershipUtilS } from '../../../features/Membership/outbound/share';
import { getActivatedSdkWithEvent } from '../../../features/Membership/util';
import { makeEmptyScreenNavigate } from '../../../shared/app/navigate';
import { navigateTo } from '../../../shared/app/util';
import { createAgreeModal } from '../../../shared/client/agreeModal';
import { Alert } from '../../../shared/components/cells/Alert/Alert';
import { autoBind } from '../../../shared/util/autoBind';
import { sendDocumentEvent } from '../../../shared/util/event';
import { downloadFileFromUrl } from '../../../shared/util/front';
import { getSharedData } from '../../../shared/util/rune';
import { makePlusUrl } from '../../../shared/util/url';
import { footer_links } from '../../components/cells/Footer/constant';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { onIntegratedPaidServiceTermsTextClick } from '../PaidServiceTerms/modal';
import klass from './CreatorShopMembershipPayment.module.scss';
import { ensureImageExtension } from '@services/MarppleShop/shared/client/file.ts';

export type CreatorShopMembershipPaymentPageData = {
  layout_options: BaseLayoutOptions;
  membership_project_id: string;
  membership_id: string;
  membership_token?: string;
  domain_name: string;
  plus_domain_name: string;
};

class CreatorShopMembershipPaymentBodyView extends View<CreatorShopMembershipPaymentPageData> {
  private _membershipErrorEnable = new MembershipErrorEnable(this);

  protected override template(): Html {
    return html` <div class="${klass.page}">
      <div class="membership-ui"></div>
    </div>`;
  }

  protected override async onRender() {
    const ui_section = $(this.element()).find('.membership-ui')?.element();
    if (ui_section) {
      const openPrivacyModal = createAgreeModal({
        title: T('Privacy Policy'),
        body:
          box.sel('is_user->type') == 'TEMP'
            ? PrivacyPopUpTmplS.signUpCheckout()
            : PrivacyPopUpTmplS.loginCheckout(),
        body_klass: ET.lang === 'kr' ? 'privacy-popup' : 'terms_body',
        style_klass: ET.lang === 'kr' ? '' : 'terms_modal_style terms_style',
        is_bottom_sheet: false,
      });

      const openTermsModal = () => {
        navigateTo(`/${ET.lang}/@/terms`, makeEmptyScreenNavigate, { blank: true });
      };

      try {
        const client = await getActivatedSdkWithEvent(this.data.membership_token);

        const widget = new MembershipPaymentWidget({
          client,
          toggleSpinner: MembershipUtilS.toggleSpinner,
          isApp: MShopUtilF.isApp(),
          isMobile: this.data.layout_options.is_mobile,
          projectId: this.data.membership_project_id,
          membershipId: this.data.membership_id,
          onGoToCreatorHomeButtonClick: this._goCreatorShopHome,
          onAccountSettingsButtonClick: this._goAccountSetting,
          onCheckMembershipInfoButtonClick: this._goMembershipDetail,
          onPaySuccess: () => {
            this._hideHeaderBackButton();
            this._updateHeaderTitle();
          },
          onClickPersonalInfoOutsourcing: createOnClickPersonalInfoOutsourcing(
            `.${CreatorShopMembershipPaymentPage}`,
          ),
          onClickThirdPartyConsent: createOnClickThirdPartyConsent(`.${CreatorShopMembershipPaymentPage}`),
          onClickInicisTerms: createOnClickInicisTerms(`.${CreatorShopMembershipPaymentPage}`),
          onClickPrivacy: openPrivacyModal,
          onClickTerms: openTermsModal,
          onClickPaidTerms: onIntegratedPaidServiceTermsTextClick,
          onError: this._membershipErrorEnable.onError,
          onScrollLock: MShopUtilF.bodyFixed$,
          onShopButtonClick: this._onShopButtonClick.bind(this),
          onMembershipCardDownloadButtonClick: this.onMembershipCardDownloadButtonClick.bind(this),
        });

        widget.render(ui_section);
      } catch (e) {
        ConfigSentryF.error(e);
        await this._membershipErrorEnable.openWidgetRenderErrorModal();
      }
    }
  }

  private _onShopButtonClick(shopUrl: string) {
    navigateTo(shopUrl, makeEmptyScreenNavigate);
  }

  private _goAccountSetting() {
    location.href = `/${ET.lang}/@/my_account?redirect_url=${encodeURIComponent(location.href)}`;
  }

  async onMembershipCardDownloadButtonClick(url: string, fileName: string) {
    await downloadFileFromUrl({
      url,
      file_name: ensureImageExtension(fileName),
      onError: async (error) => {
        error.name = 'MyMembershipDetail.onMembershipCardDownloadButtonClick';
        ConfigSentryF.error(error);
        console.error('Download failed:', error);
        await Alert.open({ title: '다운로드 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.' });
      },
      is_app: MShopUtilF.isCreatorApp(),
    });
  }

  @autoBind()
  private _goCreatorShopHome() {
    navigateTo(
      makePlusUrl(this.data.plus_domain_name, `/${ET.lang}/${this.data.domain_name}`),
      makeEmptyScreenNavigate,
    );
  }

  @autoBind()
  private _goMembershipDetail(membership_id: string) {
    navigateTo(`/${ET.lang}/@/my_memberships/detail/${membership_id}`, makeEmptyScreenNavigate);
  }

  private _hideHeaderBackButton() {
    if (this.data.layout_options.is_mobile) {
      sendDocumentEvent('header_back_button_hide');
    }
  }

  private _updateHeaderTitle() {
    if (this.data.layout_options.is_mobile) {
      sendDocumentEvent('header_center_part_update', { title: ET('mps2::membership::join_success') });
    }
  }
}

export class CreatorShopMembershipPaymentPage extends Page<CreatorShopMembershipPaymentPageData> {
  protected override template(data: CreatorShopMembershipPaymentPageData): Html {
    const sharedData = getSharedData(this);
    const { layout_options } = data;
    return html`
      <div>
        ${new LayoutView(
          {
            body: new CreatorShopMembershipPaymentBodyView(data),
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            full_width: true,
            has_bottom_tab_bar: !sharedData?.webviewapp,
            selected_bottom_tab_idx: -1,
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
